import {
  Banknote,
  Briefcase,
  Building2,
  Code2,
  CreditCard,
  Headset,
  Percent,
  Users,
  type LucideIcon,
} from "lucide-react";

import type { SettingsTranslations } from "@/shared/lang/interfaces/settings";
import type { PermissionModule } from "@/shared/types/permission.types";

import { ACCESS_CATALOG, type AccessCatalogModule } from "./accessCatalog.constants";

type AccessAreaLabelKey = Extract<keyof SettingsTranslations, `access_area_${string}`>;

/**
 * A business area: how a person running an organization thinks about access ("cards", "money"), as opposed to
 * the 31 backend modules, which are how the API authorizes. Purely presentational — the backend has no notion
 * of an area, and every grant is still one `(module, atom)` cell.
 */
export interface AccessArea {
  id: string;
  labelKey: AccessAreaLabelKey;
  /** One plain sentence on what the area covers, shown inside the panel. */
  hintKey: AccessAreaLabelKey;
  icon: LucideIcon;
  /** Catalog modules in this area, in display order. */
  modules: readonly PermissionModule[];
}

/**
 * The 31 catalog modules in eight areas. A test asserts every catalog module sits in exactly one area, so a
 * module added to the backend cannot silently disappear from the pickers.
 */
export const ACCESS_AREAS: readonly AccessArea[] = [
  {
    id: "money",
    labelKey: "access_area_money",
    hintKey: "access_area_money_hint",
    icon: Banknote,
    modules: [
      "payments",
      "wallets",
      "payout_batches",
      "payout_batches:approve",
      "wallets:transfer:sensitive",
    ],
  },
  {
    id: "cards",
    labelKey: "access_area_cards",
    hintKey: "access_area_cards_hint",
    icon: CreditCard,
    modules: ["cards", "cards:topup", "cards:transfer", "cards:sensitive", "cards:topup:sensitive"],
  },
  {
    id: "clients",
    labelKey: "access_area_clients",
    hintKey: "access_area_clients_hint",
    icon: Briefcase,
    modules: ["clients", "client_accounts"],
  },
  {
    id: "organization",
    labelKey: "access_area_organization",
    hintKey: "access_area_organization_hint",
    icon: Building2,
    modules: ["organizations", "organization_accounts", "identities"],
  },
  {
    id: "team",
    labelKey: "access_area_team",
    hintKey: "access_area_team_hint",
    icon: Users,
    modules: ["users", "roles", "policies"],
  },
  {
    id: "developers",
    labelKey: "access_area_developers",
    hintKey: "access_area_developers_hint",
    icon: Code2,
    modules: ["api_key", "organization_webhooks"],
  },
  {
    id: "fees",
    labelKey: "access_area_fees",
    hintKey: "access_area_fees_hint",
    icon: Percent,
    modules: ["fees", "fee_configurations"],
  },
  {
    id: "operations",
    labelKey: "access_area_operations",
    hintKey: "access_area_operations_hint",
    icon: Headset,
    modules: [
      "operations",
      "operations:payments",
      "operations:confirmations",
      "operations:notes",
      "operations:shipping_quotes",
      "operations:shipping_quotes_coloca",
      "operations:audit",
      "operations:public_accounts",
      "operations:notifications",
    ],
  },
];

const CATALOG_BY_ID = new Map<string, AccessCatalogModule>(ACCESS_CATALOG.map((mod) => [mod.id, mod]));

/** The catalog entries of an area, in the area's order. */
export function areaModules(area: AccessArea): AccessCatalogModule[] {
  return area.modules
    .map((id) => CATALOG_BY_ID.get(id))
    .filter((mod): mod is AccessCatalogModule => mod !== undefined);
}
